import {
  Button,
  Form,
  FormGroup,
  Modal,
  ModalVariant,
  TextInput,
} from '@patternfly/react-core'
import { ExclamationCircleIcon } from '@patternfly/react-icons'
import { ArrowBackOutline } from '@styled-icons/evaicons-outline/ArrowBackOutline'
import * as React from 'react'
import styled from 'styled-components'

import { HeaderContainer, ScreenContent } from '../content-layout'
import { BackButton, ContinueButton, Control, ControlRow } from '../controls'

const MessageContainer = styled.div`
  flex: 4;
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: start;
`

const ScreenHeader = styled.div`
  text-align: center;
  color: ${(props) => props.theme.colors.primary};
  font-size: 16pt;
  font-weight: 500;
`

interface SurveyCompleteProps {
  message: string
  handleGoBack?: () => void
  handleVerifyPin?: (pin: string) => void
  hasValidWitness?: boolean
}

interface ISurveyCompleteState {
  isModalOpen: boolean
  isVerifyPinModalOpen: boolean
  userPin: string
}

export class SurveyCompleteModal extends React.Component<
  SurveyCompleteProps,
  ISurveyCompleteState
> {
  constructor(props) {
    super(props)
    this.state = {
      isModalOpen: false,
      isVerifyPinModalOpen: false,
      userPin: '',
    }
  }

  onModalToggle = () => {
    this.setState({
      isModalOpen: !this.state.isModalOpen,
    })
  }

  onPinModalToggle = () => {
    this.setState({
      isVerifyPinModalOpen: !this.state.isVerifyPinModalOpen,
    })
  }

  renderHeader = (header?: string) => {
    if (!header) {
      return null
    }

    return (
      <HeaderContainer>
        <ScreenHeader dangerouslySetInnerHTML={{ __html: header }} />
      </HeaderContainer>
    )
  }

  renderBackButton = (goBack?: () => void) => {
    return goBack ? (
      <BackButton onClick={goBack}>
        <ArrowBackOutline size="45" />
      </BackButton>
    ) : null
  }

  isWitnessValid = (valid?: boolean) => {
    if (valid === undefined) {
      return 'default'
    } else {
      return valid ? 'success' : 'error'
    }
  }

  handlePinChange = (pin: string) => {
    this.setState({
      userPin: pin,
    })
  }

  handleUserComplete = () => {
    this.onModalToggle()
    this.onPinModalToggle()
  }

  handleSubmitPin = () => {
    this.props.handleVerifyPin(this.state.userPin)
  }

  renderPinForm = () => {
    return (
      <Form isHorizontal>
        <FormGroup
          label="PIN"
          isRequired
          fieldId="security-pin"
          validated={this.isWitnessValid(this.props.hasValidWitness)}
          helperTextInvalid="Incorrect PIN. Please try again."
          helperTextInvalidIcon={<ExclamationCircleIcon />}
          helperText="Please enter your security PIN"
        >
          <TextInput
            type="password"
            value={this.state.userPin}
            onChange={this.handlePinChange}
          />
        </FormGroup>
      </Form>
    )
  }

  render() {
    return (
      <ScreenContent>
        <MessageContainer>
          {this.renderHeader(this.props.message)}
        </MessageContainer>
        <ControlRow>
          <Control>{this.renderBackButton(this.props.handleGoBack)}</Control>
          <Control>
            <ContinueButton onClick={this.onModalToggle}>
              Complete
            </ContinueButton>
          </Control>
          <Control />
        </ControlRow>
        <Modal
          title="Complete survey."
          variant={ModalVariant.small}
          isOpen={this.state.isModalOpen}
          onClose={this.onModalToggle}
          actions={[
            <Button
              key="confirm"
              variant="primary"
              onClick={this.handleUserComplete}
            >
              Confirm
            </Button>,
            <Button key="cancel" variant="link" onClick={this.onModalToggle}>
              Cancel
            </Button>,
          ]}
        >
          <h3>
            You are about to complete the survey. Please confirm that you are
            satisfied with your responses and that you wish to complete the
            survey.
          </h3>
        </Modal>
        <Modal
          title="Verify Pin."
          variant={ModalVariant.small}
          isOpen={this.state.isVerifyPinModalOpen}
          onClose={this.onPinModalToggle}
          actions={[
            <Button
              key="confirm"
              variant="primary"
              onClick={this.handleSubmitPin}
            >
              Confirm
            </Button>,
            <Button key="cancel" variant="link" onClick={this.onPinModalToggle}>
              Cancel
            </Button>,
          ]}
        >
          {this.renderPinForm()}
        </Modal>
      </ScreenContent>
    )
  }
}
